"use client"

import { useApolloClient } from "@apollo/client"
import styled from "@emotion/styled"
import { Pagination, Tab, Tabs } from "@mui/material"
import { GetPageByRouteQuery } from "apollo"
import GET_COMPONENTS from "apollo/queries/GET_COMPONENTS"
import GET_WEBSITES from "apollo/queries/GET_WEBSITES"
import { useTranslations } from "next-intl"
import { usePathname } from "next/navigation"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import "swiper/css"
import { Container, CustomBreadcrumbs, NewText } from "ui"
import { filterClick, getBreadCrumbs, getNonUmbracoUrl, replaceRichTextLinks } from "utils"
import { extractProperty } from "utils/extractProperty"
import ArticleItem from "./ArticleItiem"
import LobbyBanner from "./LobbyBanner"
import NewLobbyButton from "./NewLobbyButton"

function CustomTabPanel(props: any) {
  const { children, value, index, ...rest } = props

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      style={{
        margin: 0,
        padding: 0,
        width: "100%",
        display: "flex",
        flexDirection: "column",
      }}
      {...rest}
    >
      {value === index && children}
    </div>
  )
}

const StyledContentContainer = styled.div``

const StyledArticlesContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 32px;
  padding-top: 32px;
  ${(props) => props.theme.breakpoints.up("md")} {
    padding-top: 40px;
    display: grid;
    gap: 40px 32px;
    grid-template-columns: 1fr 1fr 1fr;
  }
`
const StyledTop = styled.div`
  display: grid;
  gap: 16px;
  width: 100%;
  ${(props) => props.theme.breakpoints.up("md")} {
    grid-template-columns: 1fr auto;
  }
`
const StyledTopText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-self: center;
  ${(props) => props.theme.breakpoints.up("md")} {
    padding-bottom: 0;
  }
`

const StyledTopButton = styled.div`
  width: max-content;
  align-self: flex-end;
`

const StyledTabstContainer = styled(Tabs)`
  height: auto;
  min-height: 0;
  padding-bottom: 32px;
  ${(props) => props.theme.breakpoints.up("md")} {
    padding-bottom: 40px;
  }
`

const StyledPaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  margin: 20px 0 0;
  ${(props) => props.theme.breakpoints.up("md")} {
    margin: 40px 0 0;
  }

  .MuiPagination-ul {
    gap: 16px;
    li {
      max-width: 24px;
    }
  }

  .MuiPaginationItem-page {
    font-family: var(--rag);
    color: #011727;
    border-radius: 50% !important;
    font-size: 16px;
    font-weight: 400;
    min-width: 24px;
    height: 24px;
    border: 1px solid ${({ theme }) => theme.colors.lightGrey};
  }

  .MuiPaginationItem-root {
    margin: 0 !important;
    width: 24px;
    min-width: 24px;
    height: 24px;
  }
  .MuiPaginationItem-page.Mui-selected {
    background-color: ${({ theme }) => theme.colors.darkBlue4rentMarketing};
    color: ${({ theme }) => theme.colors.white};
  }
`

const StyledPagination = styled(Pagination)`
  .MuiPaginationItem-root {
    border-radius: 0;
  }
`

const StyledTab = styled(Tab)<{ active: boolean }>`
  font-size: 18px;
  padding: 0px 12px 8px 12px;
  margin: 0;
  min-width: unset;
  height: auto;
  min-height: 0;
  font-weight: ${(props) => (props.active ? "600" : "400")};
  color: ${(props) => (props.active ? "black" : props.theme.colors.gray)};
  border-bottom: ${(props) => (props.active ? `2px solid ${props.theme.colors.propBlue}` : "none")};
  ${(props) => props.theme.breakpoints.up("md")} {
    padding: 0px 8px 8px 8px;
    min-width: 110px;
    font-size: 20px;
  }
`
type Props = {
  pageData: GetPageByRouteQuery
  articles: any
  categories?: any
  locale: string
  websites: any
  graphqlUrl?: string
  initialCategory?: string
  initialPage?: number
}

type Und = string | undefined

type HeroContent = {
  desktopMedia?: any
  mobileMedia?: any
  bannerTitle?: any
  bannerText?: any
  subtitle?: any
  button?: any
  altText?: string
  title?: any
  text?: any
  componentButton?: any
}

type ArticleContent = {
  text: Und
  imageDesktop: Und
  imageMobile: Und
  title: Und
  subTitle: Und
  logo: Und
  date: Und
  url: string
  articlePicDescktop: string
  articlePicMobile: string
  articlePageInfoImage: string
  associate: any
  associateText?: string
}

type ArticleCategoryMap = Record<string, ArticleContent[]>
type ArticleConversionResult = {
  categories: ArticleCategoryMap
  categorySet: Set<string>
}
export function findBlock({
  blocks,
  type,
}: {
  blocks: { blockType: string }[] | undefined | null
  type: string
}) {
  if (!Array.isArray(blocks)) return undefined
  return blocks.find((el) => el && el.blockType === type)
}
function normalizeCategoryName(category: string) {
  return category.replace(/"/g, "״")
}
function convertArticlesData(
  edges: any,
  websites: any,
  allCategoryName: string,
): ArticleConversionResult {
  if (!Array.isArray(edges)) return { categories: {}, categorySet: new Set<string>() }
  const categories: ArticleCategoryMap = {}
  const categorySet = new Set<string>()
  const categorySeen = new Map<string, Set<string>>()
  edges.forEach((edge: any) => {
    if (!edge || !edge.node) return
    const { node } = edge
    const blocks = extractProperty(node, "blocks") as { blockType: string }[]
    const topBanner = findBlock({ blocks, type: "topBanner" })
    const articlePic = findBlock({ blocks, type: "articlePic" })
    const articleHeader = findBlock({ blocks, type: "articleHeader" })
    let rawCategory = extractProperty(articleHeader, "articleCategory") as
      | string
      | string[]
      | undefined
    rawCategory = Array.isArray(rawCategory)
      ? rawCategory?.filter((i) => i !== "נדל״ן")
      : rawCategory
    const categoriesList = Array.isArray(rawCategory)
      ? rawCategory.filter((c) => typeof c === "string" && c.trim().length > 0)
      : rawCategory && typeof rawCategory === "string" && rawCategory.trim().length > 0
      ? [rawCategory]
      : []
    const normalizedCategories = categoriesList.length
      ? Array.from(new Set(categoriesList.map((cat) => normalizeCategoryName(cat.trim()))))
      : [allCategoryName]
    normalizedCategories.forEach((cat) => {
      categorySet.add(cat)
      if (!categories[cat]) {
        categories[cat] = []
        categorySeen.set(cat, new Set<string>())
      }
    })
    const url = ((node.url as string) || "").trim()
    if (!url) return
    const content = findBlock({ blocks, type: "contentText" })
    const text = extractProperty(content, "innerText") as Und
    const imageDesktop = extractProperty(topBanner, "desktopImage") as Und
    const imageMobile = extractProperty(topBanner, "mobileImage") as Und
    const articlePicDescktop = extractProperty(articlePic, "articlePicDescktop")
    const articlePicMobile = extractProperty(articlePic, "articlePicMobile")
    // Extract Article page info image (used for mobile in lobby)
    const articlePageInfoImage = extractProperty(node, "image") as Und
    const title = extractProperty(articleHeader, "articleMainTitle") as Und
    const subTitle = extractProperty(articleHeader, "activitySubTitle") as Und
    const associate = extractProperty(articleHeader, "associate")
    let associateText
    if (Array.isArray(associate)) {
      const foundWebsite = websites?.filter((website: any) => website.id === associate[0]?.id)[0]
      associateText = foundWebsite?.namedProperties?.siteName?.value
    }
    const logo = extractProperty(articleHeader, "imageLogo") as Und
    const date = extractProperty(articleHeader, "articleDate") as Und
    const object: ArticleContent = {
      text,
      imageDesktop,
      imageMobile,
      title,
      logo,
      date,
      url,
      subTitle,
      articlePicDescktop,
      articlePicMobile,
      articlePageInfoImage: articlePageInfoImage || "",
      associate,
      associateText,
    }
    normalizedCategories.forEach((cat) => {
      const seen = categorySeen.get(cat)
      if (!seen) return
      if (seen.has(url)) return
      categories[cat].push(object)
      seen.add(url)
    })
  })
  return { categories, categorySet }
}

function parseHeroBlocksFromNode(node: any, fallbackNode?: any): HeroContent {
  const heroBannerBlocks = extractProperty(node, "heroBanner") as any
  const heroComponentBlocks = extractProperty(node, "heroComponent") as any

  const heroBannerBlockList = Array.isArray(heroBannerBlocks?.blocks)
    ? heroBannerBlocks.blocks
    : Array.isArray(heroBannerBlocks)
    ? heroBannerBlocks
    : []
  const heroComponentBlockList = Array.isArray(heroComponentBlocks?.blocks)
    ? heroComponentBlocks.blocks
    : Array.isArray(heroComponentBlocks)
    ? heroComponentBlocks
    : []

  const heroBannerBlock = findBlock({ blocks: heroBannerBlockList, type: "HeroBannerMagazine" })
  const heroComponentBlock = findBlock({
    blocks: heroComponentBlockList,
    type: "HeroBlockArticleMagazine",
  })

  const desktopMedia =
    extractProperty(heroBannerBlock, "imageDesktop") ||
    extractProperty(heroBannerBlock, "imageDscktop") ||
    extractProperty(node, "imageDesktop") ||
    extractProperty(node, "imageDscktop") ||
    extractProperty(fallbackNode, "imageDesktop") ||
    extractProperty(fallbackNode, "imageDscktop")
  const mobileMedia =
    extractProperty(heroBannerBlock, "imageMobile") ||
    extractProperty(node, "imageMobile") ||
    extractProperty(fallbackNode, "imageMobile")
  const bannerTitle =
    extractProperty(heroBannerBlock, "bannerTitle") ||
    extractProperty(node, "bannerTitle") ||
    extractProperty(fallbackNode, "bannerTitle")
  const bannerText =
    extractProperty(heroBannerBlock, "bannerText") ||
    extractProperty(node, "bannerText") ||
    extractProperty(fallbackNode, "bannerText")

  const title = extractProperty(heroComponentBlock, "title")
  const text = extractProperty(heroComponentBlock, "text")
  const componentButton = extractProperty(heroComponentBlock, "button")
  const subtitle = extractProperty(heroComponentBlock, "Subtitle")
  const button =
    extractProperty(heroBannerBlock, "button") ||
    extractProperty(node, "button") ||
    extractProperty(fallbackNode, "button")

  const altText = extractProperty(heroBannerBlock, "bannerImageAltText")

  return {
    desktopMedia,
    mobileMedia,
    bannerTitle,
    bannerText,
    subtitle,
    button: Array.isArray(button) ? button[0] : button,
    altText,
    title,
    text,
    componentButton: Array.isArray(componentButton) ? componentButton[0] : componentButton,
  }
}

function convertCategoriesData(
  categoryEdges: any,
  articleEdges: any,
  websites: any,
  allCategoryName: string,
): {
  categories: ArticleCategoryMap
  heroes: Record<string, HeroContent>
} {
  if (!Array.isArray(categoryEdges)) return { categories: {}, heroes: {} }

  const articlesById = new Map<string, any>()
  if (Array.isArray(articleEdges)) {
    articleEdges.forEach((edge: any) => {
      if (!edge?.node?.id) return
      articlesById.set(String(edge.node.id), edge.node)
    })
  }

  const categories: ArticleCategoryMap = {}

  const heroes: Record<string, HeroContent> = {}

  const processed = new Set<string>()

  const appendArticle = (articleNode: any, categoryName: string) => {
    const articleId = articleNode?.id ? String(articleNode.id) : ""
    if (!articleId || processed.has(articleId)) return

    const blocks = extractProperty(articleNode, "blocks") as { blockType: string }[]
    if (!Array.isArray(blocks)) return

    const topBanner = findBlock({ blocks, type: "topBanner" })
    const articlePic = findBlock({ blocks, type: "articlePic" })
    const articleHeader = findBlock({ blocks, type: "articleHeader" })
    const content = findBlock({ blocks, type: "contentText" })
    if (!articleHeader) return

    const url = articleNode.url as string
    const text = extractProperty(content, "innerText") as Und
    const imageDesktop = extractProperty(topBanner, "desktopImage") as Und
    const imageMobile = extractProperty(topBanner, "mobileImage") as Und
    const articlePicDescktop = extractProperty(articlePic, "articlePicDescktop")
    const articlePicMobile = extractProperty(articlePic, "articlePicMobile")
    // Extract Article page info image (used for mobile in lobby)
    const articlePageInfoImage = extractProperty(articleNode, "image") as Und
    const title = extractProperty(articleHeader, "articleMainTitle") as Und
    const subTitle = extractProperty(articleHeader, "activitySubTitle") as Und
    const associate = extractProperty(articleHeader, "associate")
    let associateText
    if (Array.isArray(associate)) {
      const foundWebsite = (websites || []).find((website: any) => website.id === associate[0]?.id)
      associateText = foundWebsite?.namedProperties?.siteName?.value
    }
    const logo = extractProperty(articleHeader, "imageLogo") as Und
    const date = extractProperty(articleHeader, "articleDate") as Und

    const object = {
      text,
      imageDesktop,
      imageMobile,
      title,
      logo,
      date,
      url,
      subTitle,
      articlePicDescktop,
      articlePicMobile,
      articlePageInfoImage: articlePageInfoImage || "",
      associate,
      associateText,
    }

    if (!categories[categoryName]) {
      categories[categoryName] = []
    }

    categories[categoryName].push(object)
    processed.add(articleId)
  }

  categoryEdges.forEach((edge: any) => {
    const node = edge?.node
    if (!node) return
    const alias = (node.contentType?.alias || "").toLowerCase()
    if (alias !== "articlescategory" && alias !== "articlecategory") return
    const categoryName = extractProperty(node, "pageTitle") || node.name || allCategoryName

    heroes[categoryName] = parseHeroBlocksFromNode(node)

    if (!Array.isArray(node.children)) return
    node.children.forEach((child: any) => {
      const articleNode = articlesById.get(String(child?.id))
      if (articleNode) appendArticle(articleNode, categoryName)
    })
  })

  return { categories, heroes }
}

// Detect if data is mock/invalid
function isMockOrInvalidData(articles: any): boolean {
  if (!articles?.data?.components?.edges) return true

  const edges = articles?.data.components.edges
  if (!Array.isArray(edges) || edges.length === 0) return true

  // Check for mock data patterns (e.g., invalid dates that show as "NaN/NaN/NaN")
  const hasMockData = edges.some((edge: any) => {
    const node = edge?.node
    if (!node) return true

    // Check if properties exist
    const properties = node?.properties
    if (!Array.isArray(properties) || properties.length === 0) return true

    // Look for invalid date values that would render as NaN
    const hasInvalidDate = properties.some((prop: any) => {
      if (prop?.propertyName === "blocks") {
        const blocks = prop?.propertyValue?.blocks
        if (Array.isArray(blocks)) {
          return blocks.some((block: any) => {
            if (block?.blockType === "articleHeader") {
              const dateProperty = block?.properties?.find(
                (p: any) => p?.propertyName === "articleDate",
              )
              return !dateProperty?.propertyValue?.dateTime
            }
            return false
          })
        }
      }
      return false
    })

    return hasInvalidDate
  })

  return hasMockData
}

function LobbyArticles({
  pageData,
  articles: initialArticles,
  categories: categoryPayload,
  locale,
  websites: initialWebsites,
  graphqlUrl,
  initialCategory,
  initialPage,
}: Props) {
  const [articles, setArticles] = useState(initialArticles)
  const [websites, setWebsites] = useState(initialWebsites)
  const [categoryData, setCategoryData] = useState(categoryPayload)
  const [isRefetching, setIsRefetching] = useState(false)
  const hasRefetched = useRef(false) // Track if we've already attempted a refetch
  const articlesRef = useRef<HTMLDivElement>(null) // Ref for scrolling to articles on page change
  const client = useApolloClient() // Use the Apollo client from provider

  // Client-side refetch if data is mock/invalid
  useEffect(() => {
    if (!graphqlUrl) return // Skip if graphqlUrl not provided (backward compatibility)

    if (hasRefetched.current) return // Only attempt refetch once

    const shouldRefetch = isMockOrInvalidData(initialArticles)

    if (shouldRefetch && !isRefetching) {
      hasRefetched.current = true // Mark that we've attempted a refetch
      setIsRefetching(true)

      const culture = locale === "en" ? "en-us" : "he-il"
      const route = `${graphqlUrl}/pages/articles/`

      // Refetch the data on client-side using the existing Apollo client
      Promise.all([
        client.query({
          query: GET_COMPONENTS({
            contentType: "contentPage",
            numberOfItems: 1000,
          }),
          variables: { route, culture },
          fetchPolicy: "network-only", // Force fresh data
        }),
        client.query({
          query: GET_COMPONENTS({
            contentType: "ArticlesCategory",
            numberOfItems: 1000,
          }),
          variables: { route, culture },
          fetchPolicy: "network-only",
        }),
        client.query({
          query: GET_WEBSITES,
          variables: { culture, generalInfoRoute: `/${culture}/shared/general-information` },
          fetchPolicy: "network-only",
        }),
      ])
        .then(([articlesResult, categoriesResult, websitesResult]) => {
          setArticles(articlesResult)
          setCategoryData(categoriesResult.data)
          setWebsites(websitesResult.data.contentDescendantsByAbsoluteRoute?.nodes || [])
        })
        .catch((error) => {
          console.error("Failed to refetch articles on client-side:", error)
        })
        .finally(() => {
          setIsRefetching(false)
        })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // Run only once on mount

  const t = useTranslations("Articles")
  const { selfObject, parents } = getBreadCrumbs(pageData, locale)
  const pageHero = parseHeroBlocksFromNode(pageData.page)
  const pathname = usePathname()
  const allCategoryName = t("allArticles")
  const initialCategoryResolved = useRef(initialCategory)
  const [value, setValue] = useState<number>(0)
  const [page, setPage] = useState<number>(initialPage ?? 1)

  // Fallback: derive categories from article metadata (used when categoryPayload is absent)
  const { categories: baseCategories, categorySet } = convertArticlesData(
    articles.data.components.edges,
    websites,
    allCategoryName,
  )

  // Primary: derive categories from CMS tree structure (articles/categoryname/children)
  const categoryResult: { categories: ArticleCategoryMap; heroes: Record<string, HeroContent> } =
    categoryData
      ? convertCategoriesData(
          categoryData?.components?.edges,
          articles.data.components.edges,
          websites,
          allCategoryName,
        )
      : { categories: {}, heroes: {} }

  // Use CMS tree categories when available, otherwise fall back to article metadata categories
  const effectiveCategories = categoryData ? categoryResult.categories : baseCategories

  // Build "All" tab from every article across all categories (deduplicated)
  const allArticles: ArticleContent[] = []
  const allSeen = new Set<string>()
  Object.values(effectiveCategories).forEach((arr) => {
    arr.forEach((article) => {
      const key = (article.url || "").trim()
      if (!key || allSeen.has(key)) return
      allSeen.add(key)
      allArticles.push(article)
    })
  })

  // Also include top-level articles (articles/articlename) that aren't children of any category
  if (categoryData) {
    Object.values(baseCategories).forEach((arr) => {
      arr.forEach((article) => {
        const key = (article.url || "").trim()
        if (!key || allSeen.has(key)) return
        allSeen.add(key)
        allArticles.push(article)
      })
    })
  }

  const categories: ArticleCategoryMap = {
    ...effectiveCategories,
    [allCategoryName]: allArticles,
  }

  const heroByCategory: Record<string, HeroContent> = {
    [allCategoryName]: pageHero,
    ...categoryResult.heroes,
  }

  // Tab names: from CMS category nodes when available, otherwise from article metadata
  const cmsCategoryKeys = Object.keys(effectiveCategories).filter((cat) => cat !== allCategoryName)
  const fallbackCategoryKeys = Array.from(categorySet).filter((cat) => cat !== allCategoryName)
  const categoryKeys = useMemo(
    () => [allCategoryName, ...(categoryData ? cmsCategoryKeys : fallbackCategoryKeys)],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allCategoryName, cmsCategoryKeys.length, fallbackCategoryKeys.length, categoryData],
  )

  // Resolve initial category from prop (on first render after categoryKeys are available)
  useEffect(() => {
    if (initialCategoryResolved.current) {
      const decodedCategory = decodeURIComponent(initialCategoryResolved.current)
      const idx = categoryKeys.findIndex(
        (cat) => cat.toLowerCase() === decodedCategory.toLowerCase(),
      )
      if (idx > 0) {
        setValue(idx)
      }
      initialCategoryResolved.current = undefined // Only apply once
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryKeys.length])

  const activeTabIndex = categoryKeys.length ? Math.min(value, categoryKeys.length - 1) : 0
  const pageSize = 9

  // Compute the base articles path (strip /category/... suffix if present)
  const getBasePath = useCallback(() => {
    const idx = pathname.indexOf("/category/")
    return idx !== -1 ? pathname.substring(0, idx) : pathname
  }, [pathname])

  const activeCategoryName = categoryKeys[activeTabIndex] || allCategoryName
  const hero = heroByCategory[activeCategoryName] || pageHero
  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    if (categoryKeys[newValue]) {
      filterClick(categoryKeys[newValue], "פילטר מגזין")
      setValue(newValue)
    }
    setPage(1)

    // Update URL to reflect the selected category
    const basePath = getBasePath()
    if (newValue === 0) {
      // "All" tab — go back to base articles path
      window.history.pushState(null, "", basePath)
    } else {
      const categorySlug = encodeURIComponent(categoryKeys[newValue])
      window.history.pushState(null, "", `${basePath}/category/${categorySlug}`)
    }
  }
  const handlePageChange = (_: React.ChangeEvent<unknown>, newPage: number) => {
    setPage(newPage)

    // Update URL with page param
    const currentPath = window.location.pathname
    if (newPage > 1) {
      window.history.replaceState(null, "", `${currentPath}?page=${newPage}`)
    } else {
      window.history.replaceState(null, "", currentPath)
    }

    setTimeout(() => {
      if (articlesRef.current) {
        const isMobile = window.innerWidth < 900
        const headerOffset = isMobile ? 84 : 118
        const top = articlesRef.current.getBoundingClientRect().top + window.scrollY - headerOffset
        window.scrollTo({ top, behavior: "smooth" })
      }
    }, 0)
  }

  // Listen for browser back/forward navigation to sync tab state
  useEffect(() => {
    const handlePopState = () => {
      const currentPath = window.location.pathname
      const categoryMatch = currentPath.match(/\/category\/([^/]+)$/)
      if (categoryMatch) {
        const decodedCategory = decodeURIComponent(categoryMatch[1])
        const idx = categoryKeys.findIndex(
          (cat) => cat.toLowerCase() === decodedCategory.toLowerCase(),
        )
        setValue(idx > 0 ? idx : 0)
      } else {
        setValue(0)
      }
      // Restore page from URL search params
      const params = new URLSearchParams(window.location.search)
      const urlPage = Number(params.get("page") || 1)
      setPage(urlPage > 0 ? urlPage : 1)
    }
    window.addEventListener("popstate", handlePopState)
    return () => window.removeEventListener("popstate", handlePopState)
  }, [categoryKeys])

  hero.title = hero.title || extractProperty(pageData.page, "pageTitle")

  const { title, text, componentButton, subtitle } = hero || {}
  return (
    <>
      {(hero?.desktopMedia || hero?.mobileMedia) && <LobbyBanner pageData={pageData} hero={hero} />}
      <Container width={1080} padding={20}>
        <CustomBreadcrumbs
          current={selfObject}
          parents={parents}
          desktopFontSize={18}
          mobileFontSize={16}
          mobileLineHeight="normal"
          desktopLineHeight="normal"
          sx={{
            paddingBlock: {
              xs: "32px",
              md: "40px",
            },
          }}
        />
        <StyledTabstContainer
          value={activeTabIndex}
          variant="scrollable"
          onChange={handleChange}
          scrollButtons="auto"
        >
          {categoryKeys.map((tab: string, index: number) => (
            <StyledTab
              active={activeTabIndex === index}
              key={`${tab}-${index.toString()}`}
              label={tab}
            />
          ))}
        </StyledTabstContainer>
        <StyledTop>
          <StyledTopText>
            <NewText
              desktopFontSize={32}
              desktopFontWeight="400"
              desktopLineHeight="36px"
              mobileFontSize={24}
              mobileFontWeight="400"
              mobileLineHeight={"normal"}
              component="h2"
              color={"black"}
            >
              {title}
            </NewText>
            {text && (
              <NewText
                desktopFontSize={20}
                desktopFontWeight="400"
                desktopLineHeight="normal"
                mobileFontSize={16}
                mobileFontWeight="400"
                mobileLineHeight={"normal"}
                component="div"
                color={"black"}
                sx={{
                  "& *": {
                    "&:first-child, &:last-child": {
                      margin: 0,
                    },
                  },
                }}
                dangerouslySetInnerHTML={{
                  __html: replaceRichTextLinks(text),
                }}
              />
            )}
            {subtitle && (
              <NewText
                desktopFontSize={16}
                desktopFontWeight="400"
                desktopLineHeight="normal"
                mobileFontSize={14}
                mobileFontWeight="400"
                mobileLineHeight={"normal"}
                component="div"
                color={"black"}
                sx={{
                  marginTop: { xs: "8px" },
                  "& *": {
                    "&:first-child, &:last-child": {
                      margin: 0,
                    },
                  },
                }}
                dangerouslySetInnerHTML={{ __html: replaceRichTextLinks(subtitle) }}
              />
            )}
          </StyledTopText>
          <StyledTopButton>
            {componentButton && componentButton.url && componentButton.name && (
              <NewLobbyButton
                variant="outlined"
                target={
                  componentButton.target
                    ? componentButton.target
                    : componentButton.type === "EXTERNAL"
                    ? "_blank"
                    : "_self"
                }
                href={getNonUmbracoUrl(componentButton.url)}
              >
                {componentButton.name}
              </NewLobbyButton>
            )}
          </StyledTopButton>
        </StyledTop>
        <StyledContentContainer ref={articlesRef}>
          {categoryKeys.map((tab: string, index: number) => (
            <CustomTabPanel key={`${tab}-${index.toString()}`} value={activeTabIndex} index={index}>
              <StyledArticlesContainer>
                {(() => {
                  const sortedArticles = [...(categories[tab] ?? [])].sort((a, b) => {
                    const dateA = new Date(a.date as any).getTime()
                    const dateB = new Date(b.date as any).getTime()
                    return dateB - dateA
                  })
                  const startIndex = (page - 1) * pageSize
                  const pagedArticles = sortedArticles.slice(startIndex, startIndex + pageSize)
                  return pagedArticles.map((i, k: number) => (
                    <ArticleItem
                      key={`${tab}-${i.url}-${k.toString()}`}
                      logo={i.logo || ""}
                      title={i.title || ""}
                      subTitle={i.subTitle || ""}
                      associationText={i.associateText}
                      date={i.date || ""}
                      imageDesktop={
                        i.articlePageInfoImage || i.articlePicDescktop || i.imageDesktop || ""
                      }
                      imageMobile={
                        i.articlePageInfoImage || i.articlePicMobile || i.imageMobile || ""
                      }
                      content={i.text || ""}
                      link={getNonUmbracoUrl(i.url)}
                      desktopShadow
                      hasLinkButton
                    />
                  ))
                })()}
              </StyledArticlesContainer>
              {categories[tab] && categories[tab].length > pageSize && (
                <StyledPaginationContainer>
                  <StyledPagination
                    page={page}
                    count={Math.ceil(categories[tab].length / pageSize)}
                    onChange={handlePageChange}
                    color="primary"
                  />
                </StyledPaginationContainer>
              )}
            </CustomTabPanel>
          ))}
        </StyledContentContainer>
      </Container>
    </>
  )
}

export default LobbyArticles
